import React, { useState, useEffect, useCallback } from 'react';
import { withStyles } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Select from '@material-ui/core/Select';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import Chip from '@material-ui/core/Chip';
import Divider from '@material-ui/core/Divider';
import { compose, get, formatDate } from '../../utility';
import styles from './agentDetails.styles';
import { withGetAgent, withUpdateAgentUser } from '../api/admin.service';
import useRequest from '../../util/RequestEsign';
import { ROLE_ID } from '../../constants';
import { withSnackbar } from '../../shared/snackbar/withSnackbar';
import { withAppUser } from '../../shared/authorization/userConsumer';
import type { UserType } from '../flowTypes';

const SUBSCRIPTION_STATUS_UNSUBSCRIBED = 0;
const SUBSCRIPTION_STATUS_SUBSCRIBED = 1;

function toDateInputValue(isoOrNull) {
  if (!isoOrNull) return '';
  const parts = String(isoOrNull).split('T');
  return parts.length >= 1 ? parts[0] : '';
}

function isSuperAdmin(user) {
  const nodes = get(user, 'systemAclsByUserId.nodes', []);
  return nodes.some(acl => Number(acl.roleId) === ROLE_ID.SUPER_ADMIN);
}

function getInitials(firstName, lastName) {
  const f = (firstName || '').trim();
  const l = (lastName || '').trim();
  if (f && l) return `${f[0]}${l[0]}`.toUpperCase();
  if (f) return f[0].toUpperCase();
  if (l) return l[0].toUpperCase();
  return '?';
}

function InfoRow({ label, value, classes }) {
  return (
    <div className={classes.infoRow}>
      <span className={classes.infoLabel}>{label}</span>
      <span className={classes.infoValue}>{value || '—'}</span>
    </div>
  );
}

type PropTypes = {
  classes: Object,
  agent: UserType,
  user: Object,
  createSnackbar: Function,
  refetchAgent: Function,
  updateUser: Function,
};

const AgentDetails = ({ classes, agent, user, createSnackbar, refetchAgent, updateUser }: PropTypes) => {
  const [subscription, setSubscription] = useState(null);
  const [editValidFrom, setEditValidFrom] = useState('');
  const [editValidTo, setEditValidTo] = useState('');
  const [editAmount, setEditAmount] = useState('');
  const [editStatus, setEditStatus] = useState(SUBSCRIPTION_STATUS_UNSUBSCRIBED);
  const [saving, setSaving] = useState(false);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editEmailVerified, setEditEmailVerified] = useState(false);
  const [editStreetNumber, setEditStreetNumber] = useState('');
  const [editStreetName, setEditStreetName] = useState('');
  const [editCity, setEditCity] = useState('');
  const [editProvince, setEditProvince] = useState('');
  const [editPostalCode, setEditPostalCode] = useState('');
  const [editCountry, setEditCountry] = useState('');
  const [savingUser, setSavingUser] = useState(false);

  const canEditSubscription = user && isSuperAdmin(user);

  useEffect(() => {
    if (!agent) return;
    setEditFirstName(agent.firstName || '');
    setEditLastName(agent.lastName || '');
    setEditEmail(agent.email || '');
    setEditPhone(agent.phone || '');
    setEditEmailVerified(agent.emailVerified === true);
    setEditStreetNumber(agent.streetNumber || '');
    setEditStreetName(agent.streetName || '');
    setEditCity(agent.city || '');
    setEditProvince(agent.province || '');
    setEditPostalCode(agent.postalCode || '');
    setEditCountry(agent.country || '');
  }, [agent]);

  const fetchSubscription = useCallback(async () => {
    const id = agent && agent.id;
    if (id == null) return;
    try {
      const data = await useRequest({ endPoint: `/user-subscription/${id}` });
      setSubscription(data);
      setEditValidFrom(toDateInputValue(data && data.validFrom));
      setEditValidTo(toDateInputValue(data && data.validTo));
      setEditAmount(data && data.amount != null ? String(data.amount) : '');
      setEditStatus(data && data.status != null ? Number(data.status) : SUBSCRIPTION_STATUS_UNSUBSCRIBED);
    } catch {
      setSubscription(null);
      setEditValidFrom('');
      setEditValidTo('');
      setEditAmount('');
      setEditStatus(SUBSCRIPTION_STATUS_UNSUBSCRIBED);
    }
  }, [agent && agent.id]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const handleSaveUser = useCallback(async () => {
    if (!agent || !canEditSubscription || !updateUser || !refetchAgent) return;
    setSavingUser(true);
    try {
      await updateUser({
        variables: {
          id: agent.id,
          user: {
            firstName: editFirstName,
            lastName: editLastName,
            email: editEmail,
            phone: editPhone || null,
            emailVerified: editEmailVerified,
            streetNumber: editStreetNumber || null,
            streetName: editStreetName || null,
            city: editCity || null,
            province: editProvince || null,
            postalCode: editPostalCode || null,
            country: editCountry || null,
          },
        },
      });
      await refetchAgent();
      createSnackbar('User updated.');
    } catch (e) {
      createSnackbar((e && e.message) || 'Failed to update user.');
    } finally {
      setSavingUser(false);
    }
  }, [agent, canEditSubscription, updateUser, refetchAgent, editFirstName, editLastName, editEmail, editPhone, editEmailVerified, editStreetNumber, editStreetName, editCity, editProvince, editPostalCode, editCountry, createSnackbar]);

  const handleSaveSubscription = useCallback(async () => {
    const id = agent && agent.id;
    if (id == null || !canEditSubscription) return;
    setSaving(true);
    try {
      await useRequest({
        endPoint: `/user-subscription/${id}`,
        method: 'PUT',
        body: {
          validFrom: editValidFrom || null,
          validTo: editValidTo || null,
          amount: editAmount === '' ? null : editAmount,
          status: editStatus,
        },
      });
      createSnackbar('Subscription updated.');
      await fetchSubscription();
    } catch (e) {
      createSnackbar((e && e.message) || 'Failed to update subscription.');
    } finally {
      setSaving(false);
    }
  }, [agent && agent.id, canEditSubscription, editValidFrom, editValidTo, editAmount, editStatus, fetchSubscription, createSnackbar]);

  const hasExistingSubscription = subscription && (
    subscription.validFrom != null
    || subscription.validTo != null
    || subscription.amount != null
    || subscription.status != null
  );

  if (!agent) return null;

  const brokerageName = get(agent, 'brokerageAclsByUserId.nodes.0.brokerageByBrokerageId.brokerageName');
  const initials = getInitials(agent.firstName, agent.lastName);
  const isSubscribed = subscription && subscription.status === SUBSCRIPTION_STATUS_SUBSCRIBED;

  return (
    <div className={classes.root}>

      {/* ── Profile header ── */}
      <Paper className={classes.profileHeader} elevation={1}>
        <div className={classes.avatar}>{initials}</div>
        <div className={classes.profileMeta}>
          <Typography className={classes.agentName}>
            {agent.firstName} {agent.lastName}
          </Typography>
          <Typography className={classes.agentEmail}>{agent.email}</Typography>
          <div className={classes.headerChips}>
            {brokerageName && (
              <Chip label={brokerageName} className={classes.chipBrokerage} />
            )}
            <Chip
              label={agent.emailVerified ? 'Email verified' : 'Email not verified'}
              className={agent.emailVerified ? classes.chipVerified : classes.chipUnverified}
            />
            {hasExistingSubscription && (
              <Chip
                label={isSubscribed ? 'Subscribed' : 'Unsubscribed'}
                className={isSubscribed ? classes.chipSubscribed : classes.chipUnsubscribed}
              />
            )}
          </div>
        </div>
      </Paper>

      {/* ── Info cards ── */}
      <div className={classes.cardGrid}>

        {/* Contact */}
        <Paper className={classes.card} elevation={1}>
          <div className={classes.cardHeader}>
            <Typography className={classes.cardTitle}>Contact</Typography>
          </div>
          <Divider className={classes.divider} />
          <div className={classes.cardBody}>
            <InfoRow label="First name" value={agent.firstName} classes={classes} />
            <InfoRow label="Last name" value={agent.lastName} classes={classes} />
            <InfoRow label="Email" value={agent.email} classes={classes} />
            <InfoRow label="Phone" value={agent.phone} classes={classes} />
            <InfoRow label="Email verified" value={agent.emailVerified ? 'Yes' : 'No'} classes={classes} />
            <InfoRow label="Brokerage" value={brokerageName} classes={classes} />
          </div>
        </Paper>

        {/* Address */}
        <Paper className={classes.card} elevation={1}>
          <div className={classes.cardHeader}>
            <Typography className={classes.cardTitle}>Address</Typography>
          </div>
          <Divider className={classes.divider} />
          <div className={classes.cardBody}>
            <InfoRow label="Street number" value={agent.streetNumber} classes={classes} />
            <InfoRow label="Street name" value={agent.streetName} classes={classes} />
            <InfoRow label="City" value={agent.city} classes={classes} />
            <InfoRow label="Province" value={agent.province} classes={classes} />
            <InfoRow label="Postal code" value={agent.postalCode} classes={classes} />
            <InfoRow label="Country" value={agent.country} classes={classes} />
          </div>
        </Paper>

      </div>

      {/* ── Subscription card ── */}
      <Paper className={classes.cardFull} elevation={1}>
        <div className={classes.cardHeader}>
          <Typography className={classes.cardTitle}>Subscription</Typography>
          {hasExistingSubscription && (
            <Chip
              label={isSubscribed ? 'Active' : 'Inactive'}
              className={isSubscribed ? classes.chipSubscribed : classes.chipUnsubscribed}
            />
          )}
        </div>
        <Divider className={classes.divider} />
        {hasExistingSubscription ? (
          <div className={classes.subscriptionStats}>
            <div className={classes.subscriptionStat}>
              <div className={classes.subscriptionStatLabel}>Amount</div>
              <div className={classes.subscriptionStatValue}>
                {subscription.amount != null ? subscription.amount : '—'}
              </div>
            </div>
            <div className={classes.subscriptionStat}>
              <div className={classes.subscriptionStatLabel}>Status</div>
              <div className={classes.subscriptionStatValue}>
                {isSubscribed ? 'Subscribed' : 'Unsubscribed'}
              </div>
            </div>
            <div className={classes.subscriptionStat}>
              <div className={classes.subscriptionStatLabel}>Valid from</div>
              <div className={classes.subscriptionStatValue}>
                {subscription.validFrom ? formatDate(subscription.validFrom) : '—'}
              </div>
            </div>
            <div className={classes.subscriptionStat}>
              <div className={classes.subscriptionStatLabel}>Valid to</div>
              <div className={classes.subscriptionStatValue}>
                {subscription.validTo ? formatDate(subscription.validTo) : '—'}
              </div>
            </div>
          </div>
        ) : (
          <div className={classes.noSubscription}>No subscription on record.</div>
        )}

        {canEditSubscription && (
          <React.Fragment>
            <Divider />
            <div className={classes.editCardHeader}>
              <Typography className={classes.editCardTitle}>
                {hasExistingSubscription ? 'Edit subscription' : 'Create subscription'}
              </Typography>
            </div>
            {!hasExistingSubscription && (
              <Typography style={{ padding: '0 24px 8px', fontSize: '0.8125rem', color: '#999' }}>
                No subscription yet — fill in the fields below and save to create one.
              </Typography>
            )}
            <div className={classes.editGrid}>
              <TextField
                label="Amount"
                value={editAmount}
                onChange={e => setEditAmount(e.target.value)}
                margin="dense"
                fullWidth
                inputProps={{ 'data-testid': 'subscription-amount' }}
              />
              <FormControl margin="dense" fullWidth>
                <InputLabel id="subscription-status-label">Status</InputLabel>
                <Select
                  labelId="subscription-status-label"
                  value={editStatus}
                  onChange={e => setEditStatus(Number(e.target.value))}
                  inputProps={{ 'data-testid': 'subscription-status' }}
                >
                  <MenuItem value={SUBSCRIPTION_STATUS_UNSUBSCRIBED}>Unsubscribed</MenuItem>
                  <MenuItem value={SUBSCRIPTION_STATUS_SUBSCRIBED}>Subscribed</MenuItem>
                </Select>
              </FormControl>
              <div />
              <TextField
                type="date"
                label="Valid from"
                value={editValidFrom}
                onChange={e => setEditValidFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ 'data-testid': 'subscription-valid-from' }}
                margin="dense"
                fullWidth
              />
              <TextField
                type="date"
                label="Valid to"
                value={editValidTo}
                onChange={e => setEditValidTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ 'data-testid': 'subscription-valid-to' }}
                margin="dense"
                fullWidth
              />
            </div>
            <div className={classes.editFooter}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveSubscription}
                disabled={saving}
              >
                {saving ? 'Saving…' : hasExistingSubscription ? 'Save subscription' : 'Create subscription'}
              </Button>
            </div>
          </React.Fragment>
        )}
      </Paper>

      {/* ── Edit user card (super admin only) ── */}
      {canEditSubscription && (
        <Paper className={classes.cardFull} elevation={1}>
          <div className={classes.editCardHeader}>
            <Typography className={classes.editCardTitle}>Edit user</Typography>
          </div>
          <Divider style={{ margin: '12px 0 0' }} />
          <div className={classes.editGrid}>
            <TextField
              label="First name"
              value={editFirstName}
              onChange={e => setEditFirstName(e.target.value)}
              margin="dense"
              fullWidth
              inputProps={{ 'data-testid': 'edit-user-first-name' }}
            />
            <TextField
              label="Last name"
              value={editLastName}
              onChange={e => setEditLastName(e.target.value)}
              margin="dense"
              fullWidth
              inputProps={{ 'data-testid': 'edit-user-last-name' }}
            />
            <TextField
              label="Email"
              type="email"
              value={editEmail}
              onChange={e => setEditEmail(e.target.value)}
              margin="dense"
              fullWidth
              inputProps={{ 'data-testid': 'edit-user-email' }}
            />
            <TextField
              label="Phone"
              value={editPhone}
              onChange={e => setEditPhone(e.target.value)}
              margin="dense"
              fullWidth
              inputProps={{ 'data-testid': 'edit-user-phone' }}
            />
          </div>
          <div className={classes.checkboxRow}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={editEmailVerified}
                  onChange={e => setEditEmailVerified(e.target.checked)}
                  color="primary"
                  inputProps={{ 'data-testid': 'edit-user-email-verified' }}
                />
              }
              label="Email verified"
            />
          </div>
          <Divider style={{ margin: '12px 0 0' }} />
          <div className={classes.editGrid}>
            <TextField
              label="Street number"
              value={editStreetNumber}
              onChange={e => setEditStreetNumber(e.target.value)}
              margin="dense"
              fullWidth
              inputProps={{ 'data-testid': 'edit-user-street-number' }}
            />
            <TextField
              label="Street name"
              value={editStreetName}
              onChange={e => setEditStreetName(e.target.value)}
              margin="dense"
              fullWidth
              inputProps={{ 'data-testid': 'edit-user-street-name' }}
            />
            <TextField
              label="City"
              value={editCity}
              onChange={e => setEditCity(e.target.value)}
              margin="dense"
              fullWidth
              inputProps={{ 'data-testid': 'edit-user-city' }}
            />
            <TextField
              label="Province"
              value={editProvince}
              onChange={e => setEditProvince(e.target.value)}
              margin="dense"
              fullWidth
              inputProps={{ 'data-testid': 'edit-user-province' }}
            />
            <TextField
              label="Postal code"
              value={editPostalCode}
              onChange={e => setEditPostalCode(e.target.value)}
              margin="dense"
              fullWidth
              inputProps={{ 'data-testid': 'edit-user-postal-code' }}
            />
            <TextField
              label="Country"
              value={editCountry}
              onChange={e => setEditCountry(e.target.value)}
              margin="dense"
              fullWidth
              inputProps={{ 'data-testid': 'edit-user-country' }}
            />
          </div>
          <div className={classes.editFooter}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveUser}
              disabled={savingUser}
            >
              {savingUser ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </Paper>
      )}

    </div>
  );
};

export default compose(
  withStyles(styles),
  withAppUser,
  withGetAgent,
  withUpdateAgentUser,
  withSnackbar,
)(AgentDetails);
